/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles/shared-styles.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { prependZero } from '../../utils/number-formatter.js';
import { timepickerStyles } from './sh-timepicker-styles.js';
import { TimeInputStateHandlerMixin } from './time-input-state-handler-mixin.js';
import { TimeInputValidatorMixin } from './time-input-validator-mixin.js';
import { TimeModifierMixin } from './time-modifier-mixin.js';

/**
 * @prop {String} amPm - AM or PM
 * @prop {String} h - hours
 * @prop {String} m - minutes
 * @prop {String} s - seconds
 * @prop {String} step - Increment when stepping up and down.
 * @prop {String} clockFormat - Either `12-hour` or `24-hour`.
 * @prop {Boolean} mini - Enables mini mode for picker which hides seconds
 * @prop {String} disabled - Disables timepicker.
 * @prop {Boolean} timeInputState - *hide
 */
export class SHTimepickerInput extends TimeInputValidatorMixin(
  TimeModifierMixin(TimeInputStateHandlerMixin(LitElement))
) {
  static get properties() {
    return {
      h: { type: String, reflect: true },
      m: { type: String, reflect: true },
      s: { type: String, reflect: true },
      amPm: { type: String, reflect: true, attribute: 'am-pm' },
      mini: { type: Boolean, reflect: true },
      step: { type: String, reflect: true },
      clockFormat: { type: String, reflect: true, attribute: 'clock-format' },
      disabled: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [timepickerStyles, sharedStyles];
  }
  render() {
    return html`
      <div class="time-input-wrapper">
        <input
          type="text"
          placeholder="hh"
          data-id="h-ip"
          id="h-label"
          min="1"
          max="${this.clockFormat === '12-hour' ? 12 : 23}"
          @keyup=${this.onKeyupValidateTimeInput}
          maxlength="2"
          .value="${this.h}"
          @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}
          size="2"
          ?disabled="${this.disabled}"
          @blur="${this._appendZeroAndUpdateTimeValue}"
        />
        <div
          class="seperator  ${!(this.h === '' && this.m === '' && this.s === '' && this.amPm === '')
            ? 'colon-active'
            : ''}"
        >
          :
        </div>
        <input
          type="text"
          placeholder="mm"
          data-id="m-ip"
          id="m-label"
          min="0"
          max="59"
          @keyup=${this.onKeyupValidateTimeInput}
          maxlength="2"
          .value="${this.m}"
          @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}
          size="2"
          ?disabled="${this.disabled}"
          @blur="${this._appendZeroAndUpdateTimeValue}"
        />
        ${!this.mini
          ? html`
              <div
                class="seperator ${!(
                  this.h === '' &&
                  this.m === '' &&
                  this.s === '' &&
                  this.amPm === ''
                )
                  ? 'colon-active'
                  : ''} full"
              >
                :
              </div>
              <input
                type="text"
                placeholder="ss"
                data-id="s-ip"
                class="full"
                id="s-label"
                min="0"
                max="59"
                @keyup=${this.onKeyupValidateTimeInput}
                maxlength="2"
                .value="${this.s}"
                @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}
                size="2"
                ?disabled="${this.disabled}"
                @blur="${this._appendZeroAndUpdateTimeValue}"
              />
            `
          : ''}
        <div
          class="seperator ${!(this.h === '' && this.m === '' && this.s === '' && this.amPm === '')
            ? 'colon-active'
            : ''}"
        ></div>
        ${this.clockFormat === '12-hour'
          ? html` <input
              type="text"
              data-id="ap-ip"
              id="ap-label"
              placeholder="AM"
              .value="${this.amPm}"
              size="2"
              maxlength="2"
              @keydown=${this._onArrowKeyUpOrDownPressUpdateTime}
              @keyup=${this.onKeyupValidateAmPm}
              ?disabled="${this.disabled}"
              @blur=${this._appendZeroAndUpdateTimeValue}
            />`
          : ''}
      </div>
    `;
  }
  constructor() {
    super();
    this.h = this.m = this.s = this.amPm = '';
    this.clockFormat = '12-hour';
    this.mini = false;
    this.step = 1;
  }
  firstUpdated() {
    if (this.h > 0 || this.m > 0 || this.s > 0 || this.amPm > 0) {
      this._updateTimeWhenInputFieldHasValue();
    }
  }
  updated(changedProperties) {
    if (changedProperties.has('timeInputState')) {
      this._handleInputFieldChanged();
    }
  }
  _handleInputFieldChanged() {
    if (this.timeInputState === 'hasValue') {
      this._updateTimeWhenInputFieldHasValue();
    }
  }
  _onArrowKeyUpOrDownPressUpdateTime(e) {
    this.incrementOrDecrementOnKeyDown(e);
  }
  _appendZeroAndUpdateTimeValue(e) {
    const element = e.target;
    const elementId = e.target.id;
    if (element.value !== '') {
      element.value = prependZero(element.value);
      switch (elementId) {
        case 'h-label':
          this.h = element.value;
          break;
        case 'm-label':
          this.m = element.value;
          break;
        case 's-label':
          this.s = element.value;
          break;
        default:
          this.amPm = element.value;
      }
    }
  }
  _updateTimeWhenInputFieldHasValue() {
    const hour = this.shadowRoot.querySelector('#h-label');
    const minute = this.shadowRoot.querySelector('#m-label');

    this._convertValueToZeroZeroIfValueIsEmptyForInput(hour);
    this._convertValueToZeroZeroIfValueIsEmptyForInput(minute);

    if (this.clockFormat === '12-hour') {
      const ampm = this.shadowRoot.querySelector('#ap-label');
      this._convertValueToAMIfValueIsEmptyForInput(ampm);
      this.amPm = ampm.value;
    }
    this.h = hour.value;
    this.m = minute.value;

    if (!this.mini) {
      const second = this.shadowRoot.querySelector('#s-label');
      this._convertValueToZeroZeroIfValueIsEmptyForInput(second);
      this.s = second.value;
    } else {
      this.s = '00';
    }
    dispatchCustomEvent(this, 'time-value-changed');
  }
  _convertValueToZeroZeroIfValueIsEmptyForInput(inputBox) {
    if (inputBox.value === '') {
      if (inputBox.id === 'h-label') {
        inputBox.value = this.getHourValueIfClockFormat24();
      } else {
        inputBox.value = '00';
      }
    }
  }
  _convertValueToAMIfValueIsEmptyForInput(amPMInputBox) {
    if (amPMInputBox.value === '') {
      amPMInputBox.value = 'AM';
    }
  }
}
customElements.define('sh-timepicker-input', SHTimepickerInput);
